import type { IconButtonProps } from '@mui/material/IconButton';

import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';

import { UserPresenceAvatarWrap } from 'src/pages/chat/components/common/avatar';

import { varTap, varHover, AnimateBorder, transitionTap } from 'src/components/animate';

// ----------------------------------------------------------------------

export type AccountButtonProps = IconButtonProps & {
  photoURL: string;
  displayName: string;
  registered?: boolean;
  /** When true with `presenceUserId`, shows corner online/offline dot (not a full border). */
  showPresenceDot?: boolean;
  presenceUserId?: string;
  presenceOnline?: boolean;
};

export function AccountButton({
  photoURL,
  displayName,
  registered: _registered,
  showPresenceDot,
  presenceUserId,
  presenceOnline,
  sx,
  ...other
}: AccountButtonProps) {
  const showDot = !!(showPresenceDot && presenceUserId);

  const avatarInner = (
    <UserPresenceAvatarWrap showPresence={showDot} isOnline={!!presenceOnline} dotOutset>
      <Avatar
        src={photoURL}
        alt={displayName}
        sx={{
          width: 34,
          height: 34,
          fontSize: 14,
        }}
      >
        {displayName?.charAt(0)}
      </Avatar>
    </UserPresenceAvatarWrap>
  );

  return (
    <IconButton
      component={m.button}
      whileTap={varTap(0.96)}
      whileHover={varHover(1.04)}
      transition={transitionTap()}
      aria-label="Account button"
      sx={[
        { p: 0 },
        ...(showDot ? [{ overflow: 'visible' as const }] : []),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {showDot ? (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            overflow: 'visible',
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
          }}
        >
          {avatarInner}
        </Box>
      ) : (
        <AnimateBorder
          sx={{ p: '3px', borderRadius: '50%', width: 40, height: 40 }}
          slotProps={{
            primaryBorder: {
              size: 60,
              width: '1px',
              sx: { color: 'primary.main' },
            },
            secondaryBorder: {
              sx: { color: 'warning.main' },
            },
          }}
        >
          {avatarInner}
        </AnimateBorder>
      )}
    </IconButton>
  );
}
